import type { Upload, UploadGroupId } from '../../../app-core/lib/upload/Upload'
import { type FileUploadData, isExtensionValid, isFileSizeValid } from '../../../app-core/lib/upload/FileUploadData'
import { type FileExtension, addDotPrefix } from '../../../shared/domain/files/fileExtensions.no-deps'
import type { FileId } from '../../../shared/domain/files/types.no-deps'
import { UploadQueue } from '../../../app-core/lib/upload/UploadQueue'
import { IDB_photos } from '../IDB/IDB.dexie'
import { IDB_uploads } from '../IDB/IDB.deviceDB'
import type { ReportClientId } from '../../../shared/domain/reports/types.no-deps'
import { photoFileNameToFileId } from '../../../shared/domain/flows/types.no-deps'
import { DS_getData } from '../DS/DS.Data'
import { User_isLoggedIn } from '../User/User.get'
import { retry } from '../../../shared/lib/retry.no-deps'

class AppUploadQueue extends UploadQueue {
    public getUpload = async (fileId: FileId) => {
        const upload = await IDB_uploads().get(fileId)
        if (!upload) {
            throw new Error('Tried to update status upload which does not exist')
        }
        return upload
    }

    public getUploads = async (groupId?: UploadGroupId) => {
        if (!User_isLoggedIn()) return []

        const { customer } = DS_getData()

        const condition = (upload: Upload) => upload.customer === customer?.id || upload.customer == null

        if (groupId) {
            return IDB_uploads().where({ groupId }).filter(condition).toArray()
        }

        return IDB_uploads().filter(condition).toArray()
    }

    public getReportUploads = async (reportId: ReportClientId) => {
        const uploads = await this.getUploads(reportId)

        // Fetch legacy photos whilst migrating
        const photos = await IDB_photos().where({ reportClientId: reportId }).toArray()

        // Fake Upload data structure
        const photoUploads = photos.map((photo): Upload => {
            return {
                status: 'Uploaded',
                fileUploadData: {
                    fileId: photoFileNameToFileId(photo.fileName),
                    fileName: photo.fileName,
                    fileSizeInBytes: -1,
                    extension: addDotPrefix(photo.extension),
                    createdAt: new Date(),
                    status: 'complete',
                    fetchUrl: photo.dataURI,
                    mimeType: 'image/jpeg',
                },
                groupId: reportId,
                canBeDeletedWhenDone: false,
            }
        })

        return uploads.concat(photoUploads)
    }

    protected setUploadState = async (upload: Upload) => {
        const { fileId } = upload.fileUploadData
        await retry(5, () => IDB_uploads().put(upload, fileId))
    }

    protected getInitialUploadState = (
        fileUploadData: FileUploadData,
        acceptedExtensions: FileExtension[],
        sizeLimitInBytes: number,
        customer: number,
        groupId?: UploadGroupId
    ): Upload => {
        if (!isExtensionValid(fileUploadData, acceptedExtensions)) {
            return {
                status: 'UploadInvalid',
                fileUploadData,
                groupId,
                reason: {
                    type: 'InvalidExtension',
                    validExtensions: acceptedExtensions,
                },
                customer,
                canBeDeletedWhenDone: false,
            }
        }

        if (!isFileSizeValid(fileUploadData, sizeLimitInBytes)) {
            return {
                status: 'UploadInvalid',
                fileUploadData,
                groupId,
                reason: {
                    type: 'FileTooBig',
                    limit: sizeLimitInBytes,
                },
                customer,
                canBeDeletedWhenDone: false,
            }
        }

        // Required for offline download the upload is first written to the disk.
        // But we want a record of it instantly.
        return {
            status: 'UploadWritingFileToDisk',
            fileUploadData,
            groupId,
            customer,
            canBeDeletedWhenDone: false,
        }
    }
}

export const appUploadQueue = new AppUploadQueue(5)
